import _ from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFilteredPatents } from 'src/filter/FilteredPatents'
import { useAgents } from 'src/agents/AgentsProvider'
import { nameOf } from 'src/agents/utils'
import { memberUrl } from '../utils'
import { Link } from 'src/navigation'
import { Agent } from 'src/agents/utils'
import TagList from 'src/components/TagList'
import LoadingButton from 'src/components/button/LoadingButton'

// Some ideas
// one could save the new links standalone, not for each member
// one could first check if there would be any changes, and only then send updates to the server

const linkTypes = [
  { label: 'owners', type: 'owner' },
  { label: 'inventors', type: 'inventor' },
  { label: 'applicants', type: 'applicant' },
]

export function MemberBulkLinking() {
  const { t } = useTranslation()
  const { members } = useFilteredPatents()
  const { agentById, agentsByMemberId, agents, postAgentLink, deleteAgentLink } = useAgents()

  const [selected, setSelected] = useState({})
  const [linkType, setLinkType] = useState(linkTypes[0].type)

  const [preview, setPreview] = useState(undefined as PreviewItem[] | undefined)

  const [tags, setTags] = useState([])
  const tagDisplays = _(agents)
    .map(a => [a.agentId, nameOf(a)])
    .fromPairs()
    .value()
  const companies = _(agents)
    .filter(a => a.agentType === 'company')
    .map(a => '' + a.agentId)
    .sortBy(id => tagDisplays[id].toLowerCase())
    .value()
  const persons = _(agents)
    .filter(a => a.agentType === 'person')
    .map(a => '' + a.agentId)
    .sortBy(id => tagDisplays[id].toLowerCase())
    .value()
  const availableTags = { Companies: companies, Persons: persons }

  const originalByMemberId: Record<number, (Agent & { name: string })[]> = _(members)
    .map(m => {
      const agents = _(agentsByMemberId[linkType]?.[m.familyMemberId] ?? [])
        .map(a => {
          const agent = agentById[a]
          if (agent) {
            return { ...agent, name: nameOf(agent) }
          } else {
            return undefined
          }
        })
        .filter(Boolean)
        .sortBy('name')
        .value()

      return [m.familyMemberId, agents]
    })
    .fromPairs()
    .value()

  const isAll = members.every(m => selected[m.familyMemberId] ?? false)
  const toggleAll = (checked: boolean) => {
    setSelected(
      _(members)
        .map(m => [m.familyMemberId, checked])
        .fromPairs()
        .value(),
    )
  }
  const anySelected = _(members).some(m => selected[m.familyMemberId])

  function createPreview() {
    const preview = _(members)
      .filter(m => selected[m.familyMemberId])
      .map(m => {
        const original = (originalByMemberId[m.familyMemberId] ?? []).map(a => a.agentId)
        return {
          familyMemberId: m.familyMemberId,
          internalReference: m.internalReference,
          original,
          linkType,
          newLinks: tags,
        }
      })
      .value()

    setPreview(preview)
  }

  async function doUpdate() {
    // Here you would typically send the update to a backend
    const toDelete = _(preview.flatMap(p => p.original.map(agentId => ({ linkType, familyMemberId: p.familyMemberId, agentId, agentType: agentById[agentId]?.agentType })))).value()
    await deleteAgentLink(toDelete)

    const toAdd = _(preview.flatMap(p => p.newLinks.map(agentId => ({ linkType, familyMemberId: p.familyMemberId, agentId, agentType: agentById[agentId]?.agentType })))).value()
    await postAgentLink(toAdd)

    setPreview(undefined)
    setSelected({})
    setTags([])
  }

  if (preview) {
    return (
      <PreviewTable
        preview={preview}
        cancel={() => setPreview(undefined)}
        doUpdate={doUpdate}
      />
    )
  }

  return (
    <div className="grid grid-cols-[auto_auto] gap-8 max-w-2xl">
      <div className="text-xl text-slate-500 font-semibold">Link these ...</div>
      <div className="text-xl text-slate-500 font-semibold">... with these</div>
      <div>
        <table>
          <thead>
            <tr className="pc-header-row">
              <th className="pc-header-cell">
                <input type="checkbox" checked={isAll} onChange={e => toggleAll(e.target.checked)} />
              </th>
              <th className="pc-header-cell">{t('reference')}</th>
              <th className="pc-header-cell">Original</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => (
              <tr key={member.familyMemberId}>
                <td className="pc-cell align-top">
                  <input
                    type="checkbox"
                    checked={selected[member.familyMemberId] ?? false}
                    onChange={e => setSelected(ss => ({ ...ss, [member.familyMemberId]: e.target.checked }))}
                  />
                </td>
                <td className="pc-cell whitespace-nowrap align-top">
                  <Link to={memberUrl(member)}>{member.internalReference}</Link>
                </td>
                <td className="pc-cell text-xs max-w-lg whitespace-normal">
                  {(originalByMemberId[member.familyMemberId] ?? []).map(a => a.name).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="border-b-2 border-pcx-300 pb-px">
          <select className="form-select py-0.5" value={linkType} onChange={e => setLinkType(e.target.value)}>
            {linkTypes.map(lt => (
              <option key={lt.type} value={lt.type}>
                {t(lt.label)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap1 pt-2">
          <TagList {...{ tags, setTags, tagDisplays, availableTags, name: linkType, placeholder: t('add') }} />
        </div>

        <div className="pt-4">
          <button className="btn-primary disabled:btn-disabled" onClick={createPreview} disabled={!anySelected}>
            Preview Update
          </button>
        </div>
      </div>
    </div>
  )
}

// data: { familyMemberId, internalReference, original, newLinks, linkType }[]

type PreviewItem = {
  familyMemberId: number
  internalReference: string
  original: number[]
  newLinks: number[]
  linkType: string
}

function PreviewTable({
  preview,
  cancel,
  doUpdate,
}: {
  preview: PreviewItem[]
  cancel: () => void
  doUpdate: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { agentById } = useAgents()

  function toNames(ids: number[]) {
    return ids
      .map(id => agentById[id])
      .filter(Boolean)
      .map(a => nameOf(a))
      .join(', ')
  }

  return (
    <>
      <div className="flex flex-row-reverse items-center gap-2">
        <LoadingButton onClick={doUpdate} className="btn-primary">Update</LoadingButton>
        <button onClick={cancel} className="btn-secondary">{t('cancel')}</button>
      </div>
      <table className="mt-4">
        <thead>
          <tr className="pc-header-row">
            <th className="pc-header-cell">Reference</th>
            <th className="pc-header-cell">Original</th>
            <th className="pc-header-cell">New Links</th>
          </tr>
        </thead>
        <tbody>
          {preview.map(item => (
            <tr key={item.familyMemberId}>
              <td className="pc-cell underline-link">
                <Link to={memberUrl({ internalReference: item.internalReference })}>{item.internalReference}</Link>
              </td>
              <td className="pc-cell text-xs line-through text-slate-600">
                {toNames(item.original)}
              </td>
              <td className="pc-cell text-xs">
                {toNames(item.newLinks)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}